import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { notificationService } from '../services/api';
import { useAuth } from './AuthContext';

const NotificationsContext = createContext(null);
const POLL_MS = 60 * 1000;
const EMPTY = { total: 0, byCategory: {} };

/**
 * Contagem de não lidas para o sininho, atualizada a cada minuto e ao voltar para a aba.
 * As listas ficam em quem as mostra (sininho e página); as marcações passam por aqui para a contagem
 * acompanhar na hora (atualização otimista) e avisam as outras listas pelo evento "notifications:changed".
 */
export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [counts, setCounts] = useState(EMPTY);

  const refresh = useCallback(async () => {
    try {
      setCounts(await notificationService.unreadCount());
    } catch {
      // Sem contagem o sininho só fica sem número
    }
  }, []);

  useEffect(() => {
    setCounts(EMPTY);
    if (!user) return undefined;
    refresh();
    const timer = setInterval(() => { if (document.visibilityState === 'visible') refresh(); }, POLL_MS);
    const onFocus = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.id, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Marca uma como lida/não lida. Devolve false se a API recusar (a contagem volta). */
  const markRead = useCallback(async (item, read) => {
    if (item.read === read) return true;
    const delta = read ? -1 : 1;
    setCounts(c => ({
      total: Math.max(0, c.total + delta),
      byCategory: { ...c.byCategory, [item.category]: Math.max(0, (c.byCategory[item.category] || 0) + delta) },
    }));
    try {
      await notificationService.mark(item.id, read);
      window.dispatchEvent(new CustomEvent('notifications:changed', { detail: { id: item.id, read } }));
      return true;
    } catch {
      refresh();
      return false;
    }
  }, [refresh]);

  /** Todas (ou as de uma categoria) como lidas. */
  const markAllRead = useCallback(async (category) => {
    const cat = category && category !== 'todas' ? category : null;
    setCounts(c => (cat
      ? { total: Math.max(0, c.total - (c.byCategory[cat] || 0)), byCategory: { ...c.byCategory, [cat]: 0 } }
      : { total: 0, byCategory: Object.fromEntries(Object.keys(c.byCategory).map(k => [k, 0])) }));
    try {
      await notificationService.markAllRead(cat);
      window.dispatchEvent(new CustomEvent('notifications:changed', { detail: { all: true, category: cat } }));
    } catch {
      refresh();
    }
  }, [refresh]);

  const value = useMemo(() => ({ counts, refresh, markRead, markAllRead }), [counts, refresh, markRead, markAllRead]);
  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  return useContext(NotificationsContext);
}

/** Aplica numa lista local uma marcação feita em outro lugar (sininho ↔ página). */
export function applyChange(items, detail) {
  if (detail.all) return items.map(n => (!detail.category || n.category === detail.category ? { ...n, read: true } : n));
  return items.map(n => (n.id === detail.id ? { ...n, read: detail.read } : n));
}
